import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../../lib/api";
import { Gauge, StatCard, TierBadge, EmpowermentBar } from "../../components/bits";
import { InspirationBanner } from "../../components/InspirationBanner";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { Users, Clock, Sparkles, Coins, Swords, Images, Award, Briefcase, CalendarCheck, UserCog } from "lucide-react";

export default function Dashboard() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/portal/dashboard").then((r) => setData(r.data)).catch(() => {});
  }, []);

  if (!data) return <div className="text-center text-mutext py-20 font-head">جارٍ تحميل لوحة التحكم …</div>;
  const { stats, rank, tier_board, monthly, challenge, challenge_joined } = data;
  const school = stats.school;
  const staffMode = stats.mode === "staff";

  return (
    <div data-testid="coordinator-dashboard" className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 data-testid="dashboard-welcome" className="font-head font-black text-2xl text-turq-900 dark:text-turq-300">بصمتك في الخير تبقى</h1>
          <p className="text-xs text-mutext">لوحة تحكم {school.name}{school.gender ? ` — مدارس ${school.gender}` : ""}</p>
        </div>
      </div>
      <InspirationBanner />

      {!staffMode && !school.total_students && (
        <div data-testid="missing-total-warning" className="bg-coral-100 text-coral-900 rounded-2xl px-5 py-4 text-sm font-bold">
          لم يتم إدخال «إجمالي عدد طلاب المدرسة» بعد — لن تتمكن من إضافة فرص تطوعية حتى إدخاله من{" "}
          <Link to="/portal/settings" className="underline">إعدادات المدرسة</Link> (منعاً للقسمة على صفر في مؤشر التمكين).
        </div>
      )}
      {staffMode && !school.staff_count && (
        <div data-testid="missing-staff-warning" className="bg-coral-100 text-coral-900 rounded-2xl px-5 py-4 text-sm font-bold">
          لم يتم إدخال «عدد منسوبي المدرسة» بعد — يحدد شريحة المنافسة العادلة، ويُدخله المدير العام من صفحة المدارس والشرائح.
        </div>
      )}

      <div className="grid lg:grid-cols-[320px_1fr] gap-5">
        <div className="bg-surface rounded-3xl p-6 shadow-md shadow-turq-900/5 border border-brd/50 text-center">
          {staffMode ? (
            <>
              <h3 className="font-head font-bold text-sm text-mutext mb-4">مشاركة المنسوبين — تطوع بلا طلاب</h3>
              <div data-testid="staff-volunteers-gauge" className="font-head font-black text-6xl text-turq-900 dark:text-turq-300">{stats.unique_students}</div>
              <div className="text-xs text-mutext mt-1">منسوب مشارك من أصل {stats.staff_count || 0} منسوباً</div>
              <div className="flex items-center justify-center gap-2 mt-4 flex-wrap">
                <TierBadge tier={stats.tier} />
              </div>
            </>
          ) : (
            <>
              <h3 className="font-head font-bold text-sm text-mutext mb-4">مؤشر التمكين — نسبة الطلبة المشاركين الفريدين</h3>
              <div className="flex justify-center"><Gauge pct={stats.pct || 0} target={90} /></div>
              <div className="flex items-center justify-center gap-2 mt-4 flex-wrap">
                <TierBadge tier={stats.tier} />
                {stats.pct >= 90 ? (
                  <span data-testid="target-reached-badge" className="text-[11px] px-3 py-1 rounded-full font-bold bg-turq-100 text-turq-900 dark:bg-surface2 dark:text-turq-300">
                    تم بلوغ المستهدف
                  </span>
                ) : (
                  <span data-testid="remaining-chip" className="text-[11px] px-3 py-1 rounded-full font-bold bg-coral-100 text-coral-900 dark:bg-surface2 dark:text-coral-300">
                    يتبقى {stats.remaining_to_target} {school.gender === "بنات" ? "طالبةً" : "طالباً"} للمستهدف
                  </span>
                )}
              </div>
            </>
          )}
          {rank.position && (
            <div data-testid="tier-rank" className="mt-4 text-xs text-mutext">
              ترتيبك في {stats.tier_info?.short ? `الشريحة ${stats.tier_info.short}` : "شريحتك"}:{" "}
              <b className="text-coral-600 font-head text-base">{rank.position}</b> من {rank.total}
            </div>
          )}
        </div>

        <div className="grid grid-cols-2 xl:grid-cols-4 gap-4 content-start">
          <StatCard icon={Users} label={staffMode ? `منسوبون مشاركون من أصل ${stats.staff_count || 0}` : (school.gender === "بنات" ? `طالبات مشاركات من أصل ${stats.total_students}` : `طلاب مشاركون فريدون من أصل ${stats.total_students}`)} value={stats.unique_students} testid="stat-unique-students" />
          <StatCard icon={Clock} label="ساعة تطوعية مرصودة" value={stats.total_hours} tone="coral" testid="stat-hours" />
          <StatCard icon={Sparkles} label="فرصة تطوعية" value={stats.opportunities} tone="beige" testid="stat-opportunities" />
          <StatCard icon={Coins} label="نقاط الأثر المرجّحة" value={stats.impact_score} sub={`وزن الشريحة ×${stats.tier_info?.weight || 1}`} testid="stat-impact-score" />
          <StatCard icon={Briefcase} label="موظفون متطوعون" value={data.staff_volunteers} tone="coral" testid="stat-staff-volunteers" />
          <StatCard icon={CalendarCheck} label="فرص قادمة / منفذة" value={`${data.opps_open} / ${data.opps_done}`} tone="beige" testid="stat-opps-status" />
          <StatCard icon={UserCog} label="إجمالي منسوبي المدرسة" value={data.staff_total} testid="stat-staff-total" />
          <div className="col-span-2 xl:col-span-4 bg-surface rounded-2xl p-5 shadow-md shadow-turq-900/5 border border-brd/50">
            <h3 className="font-head font-bold text-sm mb-4">الساعات التطوعية — آخر الأشهر</h3>
            <div className="h-44" data-testid="monthly-hours-chart">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={monthly}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--brd)" />
                  <XAxis dataKey="month" tick={{ fontSize: 10, fill: "var(--mutext)" }} />
                  <YAxis tick={{ fontSize: 10, fill: "var(--mutext)" }} />
                  <Tooltip cursor={{ fill: "transparent" }} contentStyle={{ borderRadius: 12, fontSize: 12 }} />
                  <Bar dataKey="hours" name="الساعات" fill="#12939E" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-5">
        <div className="bg-surface rounded-3xl p-6 shadow-md shadow-turq-900/5 border border-brd/50">
          <h3 className="font-head font-bold text-sm mb-4">ترتيب الشريحة — {stats.tier_info?.name || "—"}</h3>
          <div className="space-y-2">
            {tier_board.map((s, i) => (
              <div key={s.name} data-testid={`rank-row-${i}`}
                className={`flex items-center gap-3 rounded-xl px-3 py-2.5 ${s.me ? "bg-coral-100 dark:bg-surface2" : "bg-bg"}`}>
                <span className={`w-6 h-6 rounded-full text-[11px] font-black flex items-center justify-center text-white ${s.me ? "bg-coral-600" : "bg-turq-900"}`}>{i + 1}</span>
                <span className="flex-1 text-[13px] font-bold truncate">{s.name}{s.me && <span className="text-coral-600 text-[10px]"> (مدرستك)</span>}</span>
                <span className="font-head font-black text-sm text-turq-900 dark:text-turq-300">{staffMode ? `${s.impact_score} نقطة` : `${s.pct}%`}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="space-y-5">
          {challenge && (
            <div data-testid="active-challenge-card" className="rounded-3xl p-6 bg-turq-100 dark:bg-surface2 border border-turq-300/40">
              <div className="flex items-center gap-4">
                <div className="w-11 h-11 rounded-full bg-turq-900 flex items-center justify-center shrink-0">
                  <Swords className="w-5 h-5 text-white" />
                </div>
                <div className="flex-1">
                  <b className="font-head text-sm block">{challenge.title}</b>
                  <span className="text-xs text-mutext">{challenge.description}</span>
                </div>
              </div>
              <div className="mt-4 flex items-center justify-between">
                <span className="text-[11px] text-mutext">{challenge.week_start} → {challenge.week_end}</span>
                {challenge_joined ? (
                  <span data-testid="challenge-joined-badge" className="text-[11px] font-bold text-turq-900 dark:text-turq-300 bg-surface rounded-full px-4 py-1.5">أنت مشارك في التحدي</span>
                ) : (
                  <Link to="/portal/challenges" data-testid="challenge-join-link"
                    className="text-[11px] font-bold text-white bg-coral-600 hover:bg-coral-900 rounded-full px-4 py-1.5 transition-colors">
                    شارك الآن
                  </Link>
                )}
              </div>
            </div>
          )}
          <div className="grid grid-cols-2 gap-4">
            <Link to="/portal/certificates" data-testid="dash-certificates-card"
              className="bg-surface rounded-2xl p-5 shadow-md border border-brd/50 hover:shadow-lg transition-shadow">
              <Award className="w-5 h-5 text-coral-600 mb-2" />
              <div className="font-head font-black text-xl">{data.certificates_count}</div>
              <div className="text-[11px] text-mutext">شهادة في سجلك</div>
            </Link>
            <Link to="/portal/gallery" data-testid="dash-gallery-card"
              className="bg-surface rounded-2xl p-5 shadow-md border border-brd/50 hover:shadow-lg transition-shadow">
              <Images className="w-5 h-5 text-turq-600 mb-2" />
              <div className="font-head font-black text-xl">{data.gallery_count}</div>
              <div className="text-[11px] text-mutext">صورة في معرض أعمالك</div>
            </Link>
          </div>
          <div className="bg-surface rounded-2xl p-5 shadow-md border border-brd/50">
            <h3 className="font-head font-bold text-sm mb-3">أحدث الفرص</h3>
            <div className="space-y-2">
              {data.recent_opportunities.length === 0 && <div className="text-xs text-mutext">لا توجد فرص بعد — أضف أول فرصة وابدأ الأثر</div>}
              {data.recent_opportunities.map((o) => (
                <div key={o.id} className="flex items-center justify-between gap-3 text-[12.5px] bg-bg rounded-xl px-3 py-2">
                  <span className="font-bold truncate">{o.name}</span>
                  <span className="text-[10.5px] text-coral-600 font-bold shrink-0">{o.main_value}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {!staffMode && (
        <EmpowermentBar pct={stats.pct || 0} volunteers={stats.unique_students} total={stats.total_students}
          target={90} scope="في مدرستك" gender={school.gender} testid="portal-empowerment-bar" />
      )}
    </div>
  );
}
